import { useMemo } from 'react';
import { EquipmentModel, PartTypeModel } from '../../data/types';
import { useInspection } from '../useInspection';
import { ObservableLevelsEnum, TaskStatus } from '../../data/enums';
import { ProgressIndicatorService } from '../../services/progress-indicator.service';
import { inspectionDB } from '../../data/database.structure';
import progressIndicators from '../../data/local-files/progress-indicators';
import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';
import RichTextFieldHelpers from '../../helpers/richTextFieldHelpers';

const defaults = {
  summary: TaskStatus.notStarted,
  partTypesIndicators: [] as PartTypeIndicators[],
};

interface PartTypeIndicators {
  partTypeId: number;
  status: TaskStatus;
  summaryStatus: TaskStatus;
  positions: {
    positionId: string;
    status: TaskStatus;
  }[];
}

const useInspectionBucketGET = ({
  inspectionId,
  partTypes,
  bucket,
}: {
  inspectionId: string;
  partTypes: PartTypeModel[];
  bucket?: EquipmentModel;
}) => {
  const { inspection } = useInspection(inspectionId);

  const indicators = useMemo(() => {
    if (!inspection || !bucket) {
      return defaults;
    }

    const service = new ProgressIndicatorService(
      inspectionDB,
      inspection.inspection,
    );

    // Check Summary Screen
    const summaryScreenStatus = service.processScreen({
      screen: progressIndicators.Root.Bucket.children.GET.children.Summary,
      entityExternalId: bucket.externalId,
      dataLevel: ObservableLevelsEnum.equipment,
    });

    const getParts = bucket.equipmentParts.filter((part) =>
      partTypes.map((partType) => partType.id).includes(part.part.partTypeId),
    );

    // Per-part-type summary content lives in a single bucket-level observation
    // for each path, partitioned by part type: comments as "#<partTypeId>#" blocks
    // and photos as "__<partTypeId>__"-prefixed ids. Read those once here so each
    // part type's summary completion can be derived from its own content.
    const observations =
      inspection.inspection.inspectionCategoryTypes[0]?.observations ?? [];

    const summaryCommentObservation = observations.find(
      (obs) =>
        obs.dataLevelId === ObservableLevelsEnum.equipment &&
        obs.entityExternalId === bucket.externalId &&
        obs.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
            .Location.Summary.Comments,
    );
    const summaryCommentBlocks = summaryCommentObservation
      ? RichTextFieldHelpers.parseSummaries(
          summaryCommentObservation.value ?? '',
        )
      : {};

    const summaryPhotoObservations = observations.filter(
      (obs) =>
        obs.dataLevelId === ObservableLevelsEnum.equipment &&
        obs.entityExternalId === bucket.externalId &&
        obs.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
            .Location.Summary.Photo &&
        !!obs.value,
    );

    const partTypeIndicators: PartTypeIndicators[] = [];

    // Loop through part types and process each one
    for (const partType of partTypes) {
      const partTypePositions = getParts.filter(
        (part) => part.part.partTypeId === partType.id,
      );
      if (partTypePositions.length === 0) {
        continue; // Skip if no positions for this part type
      }
      const positions = partTypePositions.map((part) => ({
        positionId: part.externalId,
        status: service.processScreen({
          screen:
            progressIndicators.Root.Bucket.children.GET.children.PartType
              .children.Position,
          entityExternalId: part.externalId,
          dataLevel: ObservableLevelsEnum.equipmentPart,
        }),
      }));
      const hasSummaryComment =
        (summaryCommentBlocks[String(partType.id)] ?? '').trim().length > 0;
      const hasSummaryPhoto = summaryPhotoObservations.some((obs) =>
        (obs.value ?? '').startsWith(`__${partType.id}__`),
      );
      const summaryStatus =
        hasSummaryComment || hasSummaryPhoto
          ? TaskStatus.completed
          : TaskStatus.notStarted;

      // The part type's summary is one of its tasks alongside the positions,
      // so it must be complete for the part type to roll up to completed.
      const itemStatuses = [
        ...positions.map((pos) => pos.status),
        summaryStatus,
      ];
      const status = itemStatuses.every((s) => s === TaskStatus.completed)
        ? TaskStatus.completed
        : itemStatuses.some(
              (s) => s === TaskStatus.inProgress || s === TaskStatus.completed,
            )
          ? TaskStatus.inProgress
          : TaskStatus.notStarted;

      partTypeIndicators.push({
        partTypeId: partType.id,
        status,
        summaryStatus,
        positions,
      });
    }

    return {
      summary: summaryScreenStatus,
      partTypesIndicators: partTypeIndicators,
    };
  }, [inspectionId, inspection, bucket, partTypes]);

  const isReady = useMemo(() => {
    return !!inspection && !!bucket;
  }, [inspection, bucket]);

  return {
    ...indicators,
    isReady,
  };
};

export default useInspectionBucketGET;
